import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ERROR } from 'types';
import { CampaignInvitesFilters, CampaignInvitesSlice } from 'types/campaign/invites';
import { fetchCampaignInvitesThunk } from './thunk';

export const defaultInvitesFilters: CampaignInvitesFilters = {
    page: 1,
    q: '',
    applied: '',
    collaborated: '',
    mode: '',
};

const initialState: CampaignInvitesSlice = {
    error: null,
    loading: false,
    campaigns: {},
    applied_filter_id: null,
    filters: defaultInvitesFilters,
};

const campaignInvitesSlice = createSlice({
    name: 'campaignInvites',
    initialState,
    reducers: {
        updateInvitesFilters: (state, { payload }: PayloadAction<CampaignInvitesFilters>) => {
            state.filters = payload;
        },
        resetInvites: (state, { payload }: PayloadAction<{ campaignId?: string }>) => {
            const clearCampaignLists = (campaignId: string) => {
                const campaignData = state.campaigns[campaignId];
                if (!campaignData?.filtered_lists) {
                    delete state.campaigns[campaignId];
                    return;
                }

                const filteredLists = { ...campaignData.filtered_lists };
                for (const filterId of Object.keys(filteredLists)) {
                    filteredLists[filterId] = {
                        ...filteredLists[filterId],
                        list: [],
                        count: 0,
                        page: 1,
                        has_more: false,
                        expired: true,
                        last_fetched_at: 0,
                        filters: {
                            ...filteredLists[filterId].filters,
                            page: 1,
                        },
                    };
                }

                state.campaigns[campaignId] = {
                    ...campaignData,
                    filtered_lists: filteredLists,
                };
            };

            if (payload.campaignId) {
                clearCampaignLists(payload.campaignId);
            } else {
                Object.keys(state.campaigns).forEach(clearCampaignLists);
            }

            state.filters = { ...state.filters, page: 1 };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCampaignInvitesThunk.pending, (state) => {
                state.error = null;
                state.loading = true;
            })
            .addCase(fetchCampaignInvitesThunk.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;

                const { filterId, campaignId, filters, invites } = action.payload;
                const existingData =
                    state.campaigns?.[campaignId]?.filtered_lists?.[filterId]?.list ?? [];
                const finalList =
                    filters.page === 1 ? invites.items : [...existingData, ...invites.items];

                state.campaigns[campaignId] = {
                    ...state.campaigns[campaignId],
                    filtered_lists: {
                        ...(state.campaigns[campaignId]?.filtered_lists ?? {}),
                        [filterId]: {
                            list: finalList,
                            page: filters.page,
                            expired: false,
                            last_fetched_at: Date.now(),
                            has_more: invites.count > finalList.length,
                            filters,
                            count: invites.count,
                        },
                    },
                };
                state.applied_filter_id = filterId;
            })
            .addCase(fetchCampaignInvitesThunk.rejected, (state, { payload }) => {
                state.loading = false;
                if (payload !== ERROR.CACHED) {
                    state.error = payload || 'Failed to fetch invites!';
                }
            });
    },
});

export const { updateInvitesFilters, resetInvites } = campaignInvitesSlice.actions;
export default campaignInvitesSlice.reducer;
